import { useEffect, useSyncExternalStore } from 'react';
import {
  hasIntercomConsent,
  setIntercomLauncherVisible,
} from '@/lib/utils/intercom';
import {
  isIntercomLauncherDismissed,
  setIntercomLauncherDismissed,
  subscribeIntercomLauncherDismiss,
} from '@/lib/utils/intercomLauncherState';

/**
 * Lets the user put the Intercom bubble away.
 *
 * The launcher is pinned bottom-right, where it covers dialog and sheet footers
 * (the Importer's "Import" button, for one). Moving it left is no escape — the
 * sidebar's collapse chevron owns that corner — so the user gets a close
 * control instead. The Help button in the header brings it back.
 *
 * `visible` tracks the bubble: same conditions, so the ✕ appears with it and
 * goes with it. Clicking the ✕ flips `dismissed`, which both hides this button
 * and tells Intercom to hide the launcher.
 */
export function useIntercomLauncherDismiss() {
  const dismissed = useSyncExternalStore(
    subscribeIntercomLauncherDismiss,
    isIntercomLauncherDismissed,
    () => false,
  );

  // Intercom remembers `hide_default_launcher` per browser, so the preference
  // is re-applied on every change — including the false case, which un-hides a
  // launcher an earlier session put away.
  useEffect(() => {
    setIntercomLauncherVisible(!dismissed);
  }, [dismissed]);

  return {
    // The bubble is on screen exactly when Intercom has consent and the user
    // has not put it away — so the ✕ rides on the same two facts. Nothing else
    // gates it: not sheets, not the importer, not any overlay.
    visible: hasIntercomConsent() && !dismissed,
    dismiss: () => setIntercomLauncherDismissed(true),
  };
}
